import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { NavLink } from './navLinks';
import { GenericMegaMenu } from './GenericMegaMenu';
import { getMenuConfigByLabel, menuConfigs, MenuSectionConfig, appendProspectus } from './menuConfig';
import { useProfessionalMenuItems } from '@/hooks/navbar/useProfessionalMenuItems';
import { usePrivateMenuItems } from '@/hooks/navbar/usePrivateMenuItems';

interface DesktopNavigationProps {
  filteredNavLinks: NavLink[];
  handleFAQClick: (e: React.MouseEvent) => void;
}

export const DesktopNavigation: React.FC<DesktopNavigationProps> = ({
  filteredNavLinks,
  handleFAQClick
}) => {
  const [openItem, setOpenItem] = React.useState<string | null>(null);
  const closeTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const location = useLocation();
  
  // Récupérer les items depuis la DB
  const { data: professionalItems } = useProfessionalMenuItems();
  const { data: privateItems } = usePrivateMenuItems();

  // Créer une config dynamique qui merge les données DB avec la config statique
  const getDynamicMenuConfig = useMemo(() => {
    return (label: string): MenuSectionConfig | null => {
      let base: MenuSectionConfig | null;
      if (label.toLowerCase() === 'particuliers' && privateItems && privateItems.length > 0) {
        base = { ...menuConfigs.particuliers, items: privateItems };
      } else if (label.toLowerCase() === 'professionnels' && professionalItems && professionalItems.length > 0) {
        base = { ...menuConfigs.professionnels, items: professionalItems };
      } else {
        base = getMenuConfigByLabel(label);
      }
      // Lien "Télécharger un prospectus" ajouté sous les évènements (par audience)
      return appendProspectus(base, label);
    };
  }, [professionalItems, privateItems]);

  // Fonction pour vérifier si l'onglet doit être actif
  const isLinkActive = (link: NavLink): boolean => {
    const currentPath = location.pathname;
    
    // Vérifie le chemin exact
    if (currentPath === link.path) return true;
    
    // Vérifie les sous-chemins pour les menus avec sous-menus
    if (link.label === 'Particuliers') {
      return currentPath.startsWith('/univers-particuliers') ||
             currentPath.startsWith('/evenements') ||
             currentPath.startsWith('/nos-offres') ||
             currentPath.startsWith('/offres') ||
             currentPath.startsWith('/prospectus-particuliers');
    }
    if (link.label === 'Professionnels') {
      return currentPath.startsWith('/professionnels') ||
             currentPath.startsWith('/evenement-entreprise-prestige') ||
             currentPath.startsWith('/prospectus-professionnels');
    }
    if (link.label === 'Collectivités') {
      return currentPath.startsWith('/evenementiel-collectivites') ||
             currentPath.startsWith('/organisation-evenement-mairie') ||
             currentPath.startsWith('/ceremonies-officielles') ||
             currentPath.startsWith('/prospectus-collectivites');
    }
    if (link.label === 'Support & Documentation') {
      return currentPath.startsWith('/blog') || 
             currentPath.startsWith('/a-propos') || 
             currentPath.startsWith('/faq') ||
             currentPath.startsWith('/charte-excellence');
    }
    
    return false;
  };

  const handleMouseEnter = (path: string) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setOpenItem(path);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setOpenItem(null);
    }, 150);
  };

  // Toggle on click - useful when hover is too fast
  const handleClick = (path: string) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setOpenItem(prev => prev === path ? null : path);
  };

  return (
    <nav className="hidden md:flex items-center space-x-2">
      {filteredNavLinks.map((link) => {
        const menuConfig = getDynamicMenuConfig(link.label);
        const hasSubMenu = menuConfig !== null;

        return (
          <div
            key={link.path}
            className="relative"
            onMouseEnter={() => hasSubMenu && handleMouseEnter(link.path)}
            onMouseLeave={hasSubMenu ? handleMouseLeave : undefined}
          >
            {hasSubMenu ? (
              <>
                <button 
                  onClick={() => handleClick(link.path)}
                  className={`
                    group relative font-sans text-sm font-medium 
                    ${isLinkActive(link) || openItem === link.path ? 'text-primary' : 'text-foreground/80'}
                    hover:text-primary transition-colors duration-200
                    whitespace-nowrap inline-flex items-center justify-center 
                    px-4 py-2.5 gap-1.5 rounded-lg
                    hover:bg-primary/5
                  `}
                >
                  {link.label}
                  <ChevronDown className={`
                    h-3.5 w-3.5 transition-transform duration-200 text-primary/50
                    ${openItem === link.path ? 'rotate-180' : ''}
                  `} />
                  {/* Animated underline - always visible when active or when menu is open */}
                  <span 
                    className={`
                      absolute bottom-1 left-4 right-4 h-[2px] 
                      ${isLinkActive(link) || openItem === link.path ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}
                      transition-transform duration-200 origin-left
                      rounded-full
                    `}
                    style={{ background: '#F28C8C' }}
                  />
                </button>
                
                {/* Generic Mega Menu */}
                <GenericMegaMenu
                  isOpen={openItem === link.path}
                  onMouseEnter={() => handleMouseEnter(link.path)}
                  onMouseLeave={handleMouseLeave}
                  config={menuConfig}
                />
              </>
            ) : (
              <Link 
                to={link.path} 
                className="
                  group relative font-sans text-sm font-medium text-foreground/80 
                  hover:text-primary transition-colors duration-200
                  whitespace-nowrap inline-flex items-center justify-center 
                  px-4 py-2.5 rounded-lg
                  hover:bg-primary/5
                "
                onClick={link.onClick}
              >
                {link.label}
                {/* Animated underline */}
                <span 
                  className="
                    absolute bottom-1 left-4 right-4 h-[2px] 
                    scale-x-0 group-hover:scale-x-100 
                    transition-transform duration-200 origin-left
                    rounded-full
                  "
                  style={{ background: '#F28C8C' }}
                />
              </Link>
            )}
          </div>
        );
      })}
    </nav>
  );
};
